import { StateMachine } from "@/core/arch/state_machine";
import { ProvisioningRepository } from "./provisioning_repository";
import type { ProvisioningRequest, ProvisionStatus } from "./types";

const PAGE_SIZE = 50;
const DRAIN_LIMIT = 20;

interface ProvisioningSearch {
  type: "email" | "businessName";
  query: string;
}

interface ProvisioningListState {
  items: ProvisioningRequest[];
  loading: boolean;
  error: string | null;
  statusFilter: ProvisionStatus | "all";
  search: ProvisioningSearch;
  hasNext: boolean;
  hasPrev: boolean;
}

const initialState: ProvisioningListState = {
  items: [],
  loading: false,
  error: null,
  statusFilter: "all",
  search: { type: "businessName", query: "" },
  hasNext: false,
  hasPrev: false,
};

export class ProvisioningListBloc extends StateMachine<ProvisioningListState> {
  private _loadToken = 0;
  private _pageTokens: (string | undefined)[] = [undefined];
  private _nextToken: string | null = null;

  constructor(private repo: ProvisioningRepository) {
    super(initialState);
  }

  async load(): Promise<void> {
    this._pageTokens = [undefined];
    this._nextToken = null;
    await this._fetchCurrentPage();
  }

  async nextPage(): Promise<void> {
    if (this.state.loading) return;
    if (!this.state.hasNext || this._nextToken == null) return;
    const tokensSnapshot = [...this._pageTokens];
    this._pageTokens.push(this._nextToken);
    const ok = await this._fetchCurrentPage();
    if (!ok) this._pageTokens = tokensSnapshot;
  }

  async prevPage(): Promise<void> {
    if (this.state.loading) return;
    if (!this.state.hasPrev) return;
    const tokensSnapshot = [...this._pageTokens];
    this._pageTokens.pop();
    const ok = await this._fetchCurrentPage();
    if (!ok) this._pageTokens = tokensSnapshot;
  }

  async setStatusFilter(status: ProvisionStatus | "all"): Promise<void> {
    this.setState({ ...this.state, statusFilter: status });
    await this.load();
  }

  async setSearch(patch: Partial<ProvisioningSearch>): Promise<void> {
    this.setState({
      ...this.state,
      search: { ...this.state.search, ...patch },
    });
    await this.load();
  }

  private async _fetchCurrentPage(): Promise<boolean> {
    const token = ++this._loadToken;
    this.setState({ ...this.state, loading: true, error: null });
    const entryToken = this._pageTokens[this._pageTokens.length - 1];
    const { type, query } = this.state.search;
    const trimmed = query.trim();
    const statusFilter = this.state.statusFilter;
    const isEmailSearch = Boolean(trimmed) && type === "email";

    const fetchPage = async (
      pageToken: string | undefined,
    ): Promise<{ items: ProvisioningRequest[]; nextToken: string | null }> => {
      if (trimmed && type === "businessName") {
        const page = await this.repo.searchByBusinessName(trimmed, pageToken);
        return { items: page.items, nextToken: page.nextToken };
      }
      const page = await this.repo.listPage({
        status: statusFilter === "all" ? undefined : statusFilter,
        nextToken: pageToken,
        limit: PAGE_SIZE,
      });
      return { items: page.items, nextToken: page.nextToken };
    };

    try {
      // Email search is a single, non-paginated result set — never drained.
      if (isEmailSearch) {
        const items = await this.repo.findByEmail(trimmed);
        // eslint-disable-next-line security/detect-possible-timing-attacks -- request-sequencing counter
        if (token !== this._loadToken) return false;
        this._nextToken = null;
        this.setState({
          ...this.state,
          items,
          loading: false,
          error: null,
          hasNext: false,
          hasPrev: false,
        });
        return true;
      }

      let page = await fetchPage(entryToken);
      // eslint-disable-next-line security/detect-possible-timing-attacks -- request-sequencing counter
      if (token !== this._loadToken) return false;

      let drains = 0;
      while (
        page.items.length === 0 &&
        page.nextToken != null &&
        drains < DRAIN_LIMIT
      ) {
        page = await fetchPage(page.nextToken);
        // eslint-disable-next-line security/detect-possible-timing-attacks -- request-sequencing counter
        if (token !== this._loadToken) return false;
        drains += 1;
      }

      this._nextToken = page.nextToken;
      this.setState({
        ...this.state,
        items: page.items,
        loading: false,
        error: null,
        hasNext: this._nextToken != null,
        hasPrev: this._pageTokens.length > 1,
      });
      return true;
    } catch (error) {
      // eslint-disable-next-line security/detect-possible-timing-attacks -- request-sequencing counter
      if (token !== this._loadToken) return false;
      this._nextToken = null;
      this.setState({
        ...this.state,
        loading: false,
        error: error instanceof Error ? error.message : "Failed to load",
        hasNext: false,
        hasPrev: false,
      });
      return false;
    }
  }
}
